import Report from '__core/shared/model/report/Report';
import ReportFilter from '__core/shared/model/report/ReportFilter';
import ReportAggregation from '__core/shared/model/report/ReportAggregation';
import { ReportStatusType } from '__core/shared/model/report/Report.enum';
import stReportsList from './reportsList.component';
import stReportEditor from './reportEditor.component';
import reportController from './report.controller';

export default angular
  .module('st.Report', ['st.Core', 'angular-cache'])
  .factory('Report', () => Report)
  .factory('ReportFilter', () => ReportFilter)
  .factory('ReportAggregation', () => ReportAggregation)
  .factory('ReportStatusType', () => ReportStatusType)
  .component('stReportsList', stReportsList)
  .component('stReportEditor', stReportEditor)
  .controller('reportController', reportController)
  .service(
    'reportService',
    (
      $rootScope,
      Report,
      ReportFilter,
      ReportAggregation,
      dataService,
      ReportStatusType,
      REPORT
    ) => ({
      createReport: (doc) => dataService.createDocument(Report, doc),
      readReport: (doc) => dataService.readDocument(Report, doc),
      updateReport: (doc) => dataService.updateDocument(Report, doc),
      deleteReport: (doc) => dataService.deleteDocument(Report, doc),
      extractReportSettings: (settingContainers, valueReplacement, quick) => {
        const settings = {
          fields: _.compact(_.union(_.flatten(_.map(settingContainers, 'fields')))),
          filters: _.map(
            _.compact(_.union(_.flatten(_.map(settingContainers, 'filters')))),
            (o) => ReportFilter.build(o)
          ),
          aggregations: _.map(
            _.compact(_.union(_.flatten(_.map(settingContainers, 'aggregations')))),
            (o) => ReportAggregation.build(o)
          )
        };

        const valueType = valueReplacement || 'count';
        settings.fields = _.map(settings.fields, (f) => {
          if (f.indexOf('{valueType}') > -1) {
            return f.replace('{valueType}', valueType);
          }
          return f;
        });

        const dirtyFields = _.filter(settings.fields, (f) => f.indexOf('{counter}') > -1);
        if (!_.isEmpty(dirtyFields)) {
          let cleanFields = _.reject(settings.fields, (f) => f.indexOf('{counter}') > -1);
          for (let i = 1; i < 7; i += 1) {
            cleanFields = cleanFields.concat(
              _.map(dirtyFields, (f) => f.replace('{counter}', i))
            );
          }
          settings.fields = cleanFields;
        }

        if (quick.isLeaderBoard) {
          const timeMapFields = _.filter(
            settings.fields,
            (f) => f.indexOf('aggregation.deal.') > -1
          );
          if (!_.isEmpty(timeMapFields)) {
            const fieldMap = _.map(
              _.compact(_.union(_.flatten(_.map(settingContainers, 'fieldsMap')))),
              (o) => o
            );
            let mappedFields = [];
            _.forEach(fieldMap, (fm) => {
              mappedFields = mappedFields.concat(_.map(timeMapFields, (f) => `${f}.${fm}`));
            });
            if (!_.isEmpty(mappedFields)) {
              _.forEach(mappedFields.reverse(), (mf) => settings.fields.splice(1, 0, mf));
            }
          }
        }

        if (quick.isTicket || quick.isProduct) {
          const dirtyFilters = _.filter(settings.filters, (f) => f.term === '{timeStampTerm}');
          const status =
            _.find(REPORT.QUICK.STATUSES, (s) => s.key === _.head(quick.statuses)) ||
            _.head(REPORT.QUICK.STATUSES);
          const statusType = ReportStatusType.of(status.rel);
          if (!_.isEmpty(dirtyFilters)) {
            _.forEach(dirtyFilters, (f) => {
              const idx = _.findIndex(settings.filters, f);
              settings.filters[idx].nest = statusType.path;
              settings.filters[idx].bool = 'must';
              settings.filters[idx].term = statusType.propTime;
            });
          }
          settings.filters.push({
            nest: statusType.path,
            bool: 'must',
            term: statusType.prop,
            type: 'boolean',
            value: 'true'
          });

          const stages = _.filter(settingContainers, (s) =>
            _.some(s.fields, (f) => f.indexOf('.milestoneStage.') > -1)
          );
          if (!_.isEmpty(stages)) {
            settings.fields = _.reject(
              settings.fields,
              (f) => f.indexOf('.milestoneStage.') > -1
            );
            let hasStages = false;
            _.forEach(stages, (s) => {
              let stageId = s.key;
              if (s.fields && s.fields.length) {
                const fieldInfo = s.fields[0].split('.');
                if (fieldInfo && fieldInfo.length > 2) {
                  stageId = fieldInfo[3];
                }
              }
              hasStages = true;
              settings.filters.push({
                nest: 'relStageHistory',
                bool: 'must',
                term: 'relStageHistory.idOrIdClone',
                type: 'string',
                value: stageId
              });
            });
            const dateFilter = _.find(
              settings.filters,
              (f) => f.type === 'date' && f.term === 'timeStamp'
            );
            if (!_.isEmpty(dateFilter)) {
              const stageDateFilter = _.clone(dateFilter);
              stageDateFilter.nest = 'relStageHistory';
              stageDateFilter.term = 'relStageHistory.timeStamp';
              settings.filters.push(stageDateFilter);
              if (hasStages === true) {
                settings.filters = _.reject(
                  settings.filters,
                  (f) => f.type === 'date' && f.term === 'timeStamp'
                );
              }
            }
          }
        }
        return settings;
      },
      quickObjectsForEnterpriseGroup: () =>
        _.reject(REPORT.QUICK.OBJECTS, (o) => o.name === 'Enterprise'),
      quickObjectsForEnterpriseBranch: () =>
        _.reject(REPORT.QUICK.OBJECTS, (o) => o.name === 'Enterprise' || o.name === 'Group'),
      quickObjectsForOrganization: () =>
        _.reject(
          REPORT.QUICK.OBJECTS,
          (o) => o.name === 'Enterprise' || o.name === 'Group' || o.name === 'Branch'
        ),
      hasNestedAggregation: (report) =>
        !_.isEmpty(report.aggregations) &&
        !_.isEmpty(
          _.intersection(_.map(report.aggregations, 'group'), REPORT.AGGREGATIONS_NESTED)
        ),
      orderingField: (report) => _.first(_.intersection(report.fields, REPORT.FIELDS_ORDERING)),
      additionalMergeFields: () => {
        return [].concat(REPORT.FIELDS).concat(
          _.flatten(
            _.map(ReportStatusType.available(), (bd) => {
              const term = bd.term.toLowerCase();
              const timeMap = [
                'mn12',
                'mn11',
                'mn10',
                'mn9',
                'mn8',
                'mn7',
                'mn6',
                'mn5',
                'mn4',
                'mn3',
                'mn2',
                'mn1',
                'm0',
                'mp1',
                'mp2',
                'mp3',
                'mp4',
                'mp5',
                'mp6',
                'mp7',
                'mp8',
                'mp9',
                'mp10',
                'mp11',
                'mp12'
              ];
              return _.flatten(
                _.map(timeMap, (tm) => {
                  const sign = '-';
                  const time = tm.onlyNumbers();
                  let monthName = '';
                  if (tm.indexOf('mn') > -1) {
                    monthName = moment
                      .utc()
                      .tz($rootScope.CurrentTimeZone() ? $rootScope.CurrentTimeZone() : 'UTC')
                      .subtract(time, 'months')
                      .format('MMM YYYY');
                  } else if (tm.indexOf('mp') > -1) {
                    monthName = moment
                      .utc()
                      .tz($rootScope.CurrentTimeZone() ? $rootScope.CurrentTimeZone() : 'UTC')
                      .add(time, 'months')
                      .format('MMM YYYY');
                  } else {
                    monthName = moment
                      .utc()
                      .tz($rootScope.CurrentTimeZone() ? $rootScope.CurrentTimeZone() : 'UTC')
                      .add(time, 'months')
                      .format('MMM YYYY');
                  }
                  return [
                    {
                      name: `Deal - Aggregated Values - ${monthName} ${bd.term} count`,
                      key: `aggregation.deal.${term}.count.${tm}`,
                      type: 'number'
                    },
                    {
                      name: `Deal - Aggregated Values - ${monthName} ${bd.term} value`,
                      key: `aggregation.deal.${term}.sum.${tm}`,
                      type: 'currency'
                    },
                    {
                      name: `Deal - Aggregated Values - ${monthName} Created count`,
                      key: `aggregation.deal.count.${tm}`,
                      type: 'number'
                    },
                    {
                      name: `Deal - Aggregated Values - ${monthName} Created value`,
                      key: `aggregation.deal.sum.${tm}`,
                      type: 'currency'
                    }
                  ];
                })
              ).concat([
                {
                  name: `Deal - Aggregated Values - ${bd.term} count`,
                  key: `aggregation.deal.${term}.count`,
                  type: 'number'
                },
                {
                  name: `Deal - Aggregated Values - ${bd.term} value`,
                  key: `aggregation.deal.${term}.sum`,
                  type: 'currency'
                }
              ]);
            })
          )
        );
      },
      simplifyMergeFields: (fields, mergeFields, additionalFields) => {
        const allFieldsValues = _.compact(
          [].concat(_.flatten(_.map(mergeFields, 'value'))).concat(additionalFields)
        );
        /* eslint-disable no-param-reassign */
        return _.compact(
          _.map(fields, (field) =>
            _.find(allFieldsValues, (fieldValue) => fieldValue.key === field)
          )
        );
        /* eslint-enable no-param-reassign */
      }
    })
  )
  .service('reportsService', ($http, $rootScope, CacheFactory, dataService, Report, REPORT) => {
    let cache;
    if (!CacheFactory.get(REPORT.CACHE.NAME)) {
      cache = CacheFactory(REPORT.CACHE.NAME, REPORT.CACHE.STORE);
    }
    const cacheKey = (params, requestName) =>
      JSON.stringify({
        idAccount: $rootScope.CurrentAccount.id,
        idOrganization: $rootScope.CurrentOrganization.id,
        type: requestName || REPORT.CACHE.NAME,
        params
      });
    return {
      clearCache: () => cache.removeAll(),
      getAvailable: (params) =>
        dataService.apiRequest(
          cache,
          cacheKey(params, 'getAvailable'),
          params?.ignoreCache === true,
          Report.build,
          $http.get,
          REPORT.API.AVAILABLE,
          { params }
        ),
      getWorkflowReport: (params) =>
        dataService.apiRequest(
          cache,
          cacheKey(params, 'getWorkflowReport'),
          true,
          null,
          $http.post,
          REPORT.API.WORKFLOW,
          { params }
        ),
      exportWorkflowReport: (params) =>
        dataService
          .apiRequest(null, null, true, null, $http.post, REPORT.API.WORKFLOW_EXPORT, {
            params,
            __requestConfiguration: {
              responseType: 'arraybuffer',
              transformResponse: (response) => ({
                response: response
                  ? new Blob([response], { type: 'application/octet-stream' })
                  : null
              })
            }
          })
          .then((data) => {
            saveAs(data.response, `${params.name}.xlsx`.replace(/[^a-zA-Z0-9. ]+/gi, ''));
          })
    };
  })
  .constant('REPORT', {
    API: {
      AVAILABLE: '/api/v1/reports/available/',
      WORKFLOW: '/api/v1/tickets/workflow-report/',
      WORKFLOW_EXPORT: '/api/v1/tickets/workflow-report-export/'
    },
    DEFAULT: {
      aggregations: [{ term: 'idOwner', type: 'aggAccount' }],
      filters: [
        {
          term: '{timeStampTerm}',
          type: 'date',
          value: { from: 'now/M', to: 'now+1M/M' }
        },
        {
          term: 'isActive',
          type: 'boolean',
          value: 'true'
        }
      ],
      fields: [
        'deal.owner.nameFull',
        'aggregation.deal.created.sum.m0',
        'aggregation.deal.won.sum.m0',
        'aggregation.deal.lost.sum.m0',
        'aggregation.deal.expected.sum.m0',
        'aggregation.deal.created.sum',
        'aggregation.deal.won.sum',
        'aggregation.deal.lost.sum',
        'aggregation.deal.expected.sum'
      ]
    },
    FILTERS: [
      {
        key: 'date.created',
        rel: 'CREATED',
        name: 'Created',
        type: 'date',
        filters: [
          {
            term: 'timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.stale',
        rel: 'STALE',
        name: 'Stale',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isRotten',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.frozen',
        rel: 'PAUSED',
        name: 'Paused',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isFrozen',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.lodged',
        rel: 'LODGED',
        name: 'Lodged',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isLodged',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.submitted',
        rel: 'SUBMITTED',
        name: 'Submitted',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isSubmitted',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.conditionallyApproved',
        rel: 'CONDITIONALLY_APPROVED',
        name: 'Conditionally approved',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isConditionallyApproved',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.approved',
        rel: 'APPROVED',
        name: 'Approved',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isApproved',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.settled',
        rel: 'SETTLED',
        name: 'Settled',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isSettled',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.won',
        rel: 'WON',
        name: 'Won',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isWon',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.withdrawn',
        rel: 'WITHDRAWN',
        name: 'Withdrawn',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isWithDrawn',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.declined',
        rel: 'DECLINED',
        name: 'Declined',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isDeclined',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'date.lost',
        rel: 'LOST',
        name: 'Lost',
        type: 'date',
        filters: [
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.isLost',
            type: 'boolean',
            value: 'true'
          },
          {
            nest: 'relStatusHistory',
            bool: 'must',
            term: 'relStatusHistory.timeStamp',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      },
      {
        key: 'filter.state',
        rel: '',
        name: 'Organization State',
        type: 'string',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'relOrganization.organization.address.state',
            type: 'string',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.stale',
        rel: 'STALE',
        name: 'Stale',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isRotten',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.frozen',
        rel: 'PAUSED',
        name: 'Paused',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isFrozen',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.lodged',
        rel: 'LODGED',
        name: 'Lodged',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isLodged',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.submitted',
        rel: 'SUBMITTED',
        name: 'Submitted',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isSubmitted',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.conditionallyApproved',
        rel: 'CONDITIONALLY_APPROVED',
        name: 'Conditionally approved',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isConditionallyApproved',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.approved',
        rel: 'APPROVED',
        name: 'Approved',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isApproved',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.settled',
        rel: 'SETTLED',
        name: 'Settled',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isSettled',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.won',
        rel: 'WON',
        name: 'Won',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isWon',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.withdrawn',
        rel: 'WITHDRAWN',
        name: 'Withdrawn',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isWithdrawn',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.declined',
        rel: 'DECLINED',
        name: 'Declined',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isDeclined',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'status.lost',
        rel: 'LOST',
        name: 'Lost',
        type: 'boolean',
        filters: [
          {
            nest: '',
            bool: '',
            term: 'isLost',
            type: 'boolean',
            value: 'true'
          }
        ]
      },
      {
        key: 'date.expected',
        rel: 'EXPECTED',
        name: 'Expected',
        type: 'date',
        filters: [
          {
            term: 'values.onceOffDueDate',
            type: 'date',
            value: { from: 'now-12M/M', to: 'now/M' }
          }
        ]
      }
    ],
    AGGREGATIONS: [
      {
        key: 'aggregation.enterprise',
        name: 'Enterprise',
        fields: ['organization.enterpriseName'],
        aggregations: [{ term: 'relOrganization.enterpriseName', type: 'aggEnterprise' }]
      },
      {
        key: 'aggregation.group',
        name: 'Group',
        fields: ['organization.groupName'],
        aggregations: [{ term: 'relOrganization.groupName', type: 'aggGroup' }]
      },
      {
        key: 'aggregation.branch',
        name: 'Branch',
        fields: ['organization.name'],
        aggregations: [{ term: 'relOrganization.organization.name', type: 'aggBranch' }]
      },
      {
        key: 'aggregation.state',
        name: 'State',
        fields: ['organization.address.state'],
        aggregations: [{ term: 'relOrganization.organization.address.state', type: 'aggState' }]
      },
      {
        key: 'aggregation.user',
        name: 'User',
        fields: ['deal.owner.nameFull'],
        aggregations: [{ term: 'idOwner', type: 'aggAccount' }]
      },
      {
        key: 'aggregation.partner',
        name: 'Partner',
        fields: ['deal.partner1.nameFull'],
        aggregations: [{ term: 'idPartners', type: 'aggPartner' }]
      },
      {
        key: 'aggregation.lender',
        name: 'Lender',
        fields: ['brokerTools.preferredProduct.lender'],
        aggregations: [{ term: 'relApplication.lenderShorthand', type: 'aggLender' }]
      }
    ],
    AGGREGATIONS_NESTED: ['aggAccountByLender', 'aggLenderByAccount'],
    QUICK: {
      OBJECTS: [
        {
          key: 'object.enterprise',
          name: 'Enterprise',
          fields: ['organization.enterpriseName'],
          aggregations: [{ term: 'relOrganization.enterpriseName', type: 'aggEnterprise' }]
        },
        {
          key: 'object.group',
          name: 'Group',
          fields: ['organization.groupName'],
          aggregations: [{ term: 'relOrganization.groupName', type: 'aggGroup' }]
        },
        {
          key: 'object.branch',
          name: 'Branch',
          fields: ['organization.name'],
          aggregations: [{ term: 'relOrganization.organization.name', type: 'aggBranch' }]
        },
        {
          key: 'object.user',
          name: 'User',
          fields: ['deal.owner.nameFull'],
          aggregations: [{ term: 'idOwner', type: 'aggAccount' }]
        },
        {
          key: 'object.partner',
          name: 'Partner',
          fields: ['deal.partner1.nameFull'],
          aggregations: [{ term: 'idPartners', type: 'aggPartner' }]
        },
        {
          key: 'object.lender',
          name: 'Lender',
          fields: ['brokerTools.preferredProduct.lender'],
          aggregations: [{ term: 'relApplication.lenderShorthand', type: 'aggLender' }]
        }
      ],
      TIME: [
        {
          key: 'time.last12Months',
          name: 'Previous 12 months',
          fieldsMap: [
            'mn12',
            'mn11',
            'mn10',
            'mn9',
            'mn8',
            'mn7',
            'mn6',
            'mn5',
            'mn4',
            'mn3',
            'mn2',
            'mn1'
          ],
          filters: [
            {
              term: '{timeStampTerm}',
              type: 'date',
              value: { from: 'now-12M/M', to: 'now/M' }
            }
          ]
        },
        {
          key: 'time.last6Months',
          name: 'Previous 6 months',
          fieldsMap: ['mn6', 'mn5', 'mn4', 'mn3', 'mn2', 'mn1'],
          filters: [
            {
              term: '{timeStampTerm}',
              type: 'date',
              value: { from: 'now-6M/M', to: 'now/M' }
            }
          ]
        },
        {
          key: 'time.last3Months',
          name: 'Previous 3 months',
          fieldsMap: ['mn3', 'mn2', 'mn1'],
          filters: [
            {
              term: '{timeStampTerm}',
              type: 'date',
              value: { from: 'now-3M/M', to: 'now/M' }
            }
          ]
        },
        {
          key: 'time.lastMonth',
          name: 'Previous Month',
          fieldsMap: ['mn1'],
          filters: [
            {
              term: '{timeStampTerm}',
              type: 'date',
              value: { from: 'now-1M/M', to: 'now/M' }
            }
          ]
        },
        {
          key: 'time.thisMonth',
          name: 'This Month',
          fieldsMap: ['m0'],
          filters: [
            {
              term: '{timeStampTerm}',
              type: 'date',
              value: { from: 'now/M', to: 'now+1M/M' }
            }
          ]
        },
        {
          key: 'time.anyTime',
          name: 'Any time'
        },
        {
          key: 'time.nextMonth',
          name: 'Next Month',
          fieldsMap: ['mp1'],
          filters: [
            {
              term: '{timeStampTerm}',
              type: 'date',
              value: { from: 'now+1M/M', to: 'now+2M/M' }
            }
          ]
        },
        {
          key: 'time.next3Months',
          name: 'Next 3 months',
          fieldsMap: ['mp1', 'mp2', 'mp3'],
          filters: [
            {
              term: '{timeStampTerm}',
              type: 'date',
              value: { from: 'now+1M/M', to: 'now+4M/M' }
            }
          ]
        },
        {
          key: 'time.next6Months',
          name: 'Next 6 months',
          fieldsMap: ['mp1', 'mp2', 'mp3', 'mp4', 'mp5', 'mp6'],
          filters: [
            {
              term: '{timeStampTerm}',
              type: 'date',
              value: { from: 'now+1M/M', to: 'now+7M/M' }
            }
          ]
        },
        {
          key: 'time.next12Months',
          name: 'Next 12 months',
          fieldsMap: [
            'mp1',
            'mp2',
            'mp3',
            'mp4',
            'mp5',
            'mp6',
            'mp7',
            'mp8',
            'mp9',
            'mp10',
            'mp11',
            'mp12'
          ],
          filters: [
            {
              term: '{timeStampTerm}',
              type: 'date',
              value: { from: 'now+1M/M', to: 'now+13M/M' }
            }
          ]
        }
      ],
      STATUSES: [
        {
          key: 'status.open',
          rel: 'CREATED',
          name: 'Created',
          fields: ['aggregation.deal.created.{valueType}']
        },
        {
          key: 'status.stale',
          rel: 'STALE',
          name: 'Stale',
          fields: ['aggregation.deal.stale.{valueType}']
        },
        {
          key: 'status.frozen',
          rel: 'PAUSED',
          name: 'Paused',
          fields: ['aggregation.deal.paused.{valueType}']
        },
        {
          key: 'status.lodged',
          rel: 'LODGED',
          name: 'Lodged',
          fields: ['aggregation.deal.lodged.{valueType}']
        },
        {
          key: 'status.submitted',
          rel: 'SUBMITTED',
          name: 'Submitted',
          fields: ['aggregation.deal.submitted.{valueType}']
        },
        {
          key: 'status.conditionallyApproved',
          rel: 'CONDITIONALLY_APPROVED',
          name: 'Conditionally approved',
          fields: ['aggregation.deal.conditionallyapproved.{valueType}']
        },
        {
          key: 'status.approved',
          rel: 'APPROVED',
          name: 'Approved',
          fields: ['aggregation.deal.approved.{valueType}']
        },
        {
          key: 'status.settled',
          rel: 'SETTLED',
          name: 'Settled',
          fields: ['aggregation.deal.settled.{valueType}']
        },
        {
          key: 'status.won',
          rel: 'WON',
          name: 'Won',
          fields: ['aggregation.deal.won.{valueType}']
        },
        {
          key: 'status.withdrawn',
          rel: 'WITHDRAWN',
          name: 'Withdrawn',
          fields: ['aggregation.deal.withdrawn.{valueType}']
        },
        {
          key: 'status.declined',
          rel: 'DECLINED',
          name: 'Declined',
          fields: ['aggregation.deal.declined.{valueType}']
        },
        {
          key: 'status.lost',
          rel: 'LOST',
          name: 'Lost',
          fields: ['aggregation.deal.lost.{valueType}']
        }
      ],
      TICKET: [
        {
          key: 'deal.deal',
          fields: [
            'deal.name',
            'contact1.nameFull',
            'contact2.nameFull',
            'deal.owner.nameFull',
            'organization.name',
            'organization.address.state',
            'deal.value',
            'deal.loan.lender',
            'workflow.name',
            'stage.name',
            'deal.dateSettled'
          ],
          filters: []
        }
      ],
      PRODUCT: [
        {
          key: 'deal.deal',
          fields: [
            'deal.name',
            'contact1.nameFull',
            'contact2.nameFull',
            'deal.owner.nameFull',
            'organization.name',
            'organization.address.state',
            'deal.loan.lender',
            'deal.dateSettled',
            'deal.loan.details.loanSplit{counter}.product',
            'deal.loan.details.loanSplit{counter}.amount',
            'deal.loan.details.loanSplit{counter}.loanType',
            'deal.loan.details.loanSplit{counter}.interestRate',
            'deal.loan.details.loanSplit{counter}.interestRateDiscount',
            'deal.loan.details.loanSplit{counter}.interestRateOfProduct',
            'deal.loan.details.loanSplit{counter}.interestRateType',
            'deal.loan.details.loanSplit{counter}.fixedYears',
            'deal.loan.details.loanSplit{counter}.ioYears',
            'deal.loan.details.loanSplit{counter}.repaymentType',
            'deal.loan.details.loanSplit{counter}.loanAccountNumber'
          ],
          filters: []
        }
      ],
      ANNIVERSARY: [
        {
          key: 'anniversary.none',
          name: 'None',
          fields: [
            'relContact.owner',
            'relContact.name',
            'relContact.nameGiven',
            'relContact.nameFamily',
            'relContact.namePreferred',
            'relContact.birthday',
            'relContact.phonePrimary',
            'relContact.emailPrimary',
            'relContact.addressCurrent',
            'relContact.addressMailing'
          ],
          filters: []
        },
        {
          key: 'anniversary.dob',
          name: 'Birthday',
          fields: [
            'relContact.owner',
            'relContact.name',
            'relContact.nameGiven',
            'relContact.nameFamily',
            'relContact.namePreferred',
            'relContact.birthday',
            'relContact.phonePrimary',
            'relContact.emailPrimary',
            'relContact.addressCurrent',
            'relContact.addressMailing',
            'relAnniversaries.type',
            'relAnniversaries.name',
            'relAnniversaries.timeStamp',
            'relAnniversaries.daysDifference'
          ],
          filters: [
            {
              nest: 'relAnniversaries',
              bool: 'must',
              term: 'relAnniversaries.type',
              type: 'string',
              value: 'DOB'
            },
            {
              nest: 'relAnniversaries',
              bool: 'must',
              term: 'relAnniversaries.month',
              type: 'number',
              value: { from: `{timeStampMonth}`, to: `{timeStampMonth}` }
            }
          ]
        },
        {
          key: 'anniversary.fix',
          name: 'Fixed expiry',
          fields: [
            'relContact.owner',
            'relContact.name',
            'relContact.nameGiven',
            'relContact.nameFamily',
            'relContact.namePreferred',
            'relContact.birthday',
            'relContact.phonePrimary',
            'relContact.emailPrimary',
            'relContact.addressCurrent',
            'relContact.addressMailing',
            'relAnniversaries.type',
            'relAnniversaries.name',
            'relAnniversaries.timeStamp',
            'relAnniversaries.daysDifference'
          ],
          filters: [
            {
              nest: 'relAnniversaries',
              bool: 'must',
              term: 'relAnniversaries.type',
              type: 'string',
              value: 'FIX'
            },
            {
              nest: 'relAnniversaries',
              bool: 'must',
              term: 'relAnniversaries.timeStamp',
              type: 'date',
              value: { from: `{timeStampTerm}`, to: `{timeStampTerm}` }
            }
          ]
        },
        {
          key: 'anniversary.io',
          name: 'IO expiry',
          fields: [
            'relContact.owner',
            'relContact.name',
            'relContact.nameGiven',
            'relContact.nameFamily',
            'relContact.namePreferred',
            'relContact.birthday',
            'relContact.phonePrimary',
            'relContact.emailPrimary',
            'relContact.addressCurrent',
            'relContact.addressMailing',
            'relAnniversaries.type',
            'relAnniversaries.name',
            'relAnniversaries.timeStamp',
            'relAnniversaries.daysDifference'
          ],
          filters: [
            {
              nest: 'relAnniversaries',
              bool: 'must',
              term: 'relAnniversaries.type',
              type: 'string',
              value: 'IO'
            },
            {
              nest: 'relAnniversaries',
              bool: 'must',
              term: 'relAnniversaries.timeStamp',
              type: 'date',
              value: { from: `{timeStampTerm}`, to: `{timeStampTerm}` }
            }
          ]
        },
        {
          key: 'anniversary.ins',
          name: 'Insurance due',
          fields: [
            'relContact.owner',
            'relContact.name',
            'relContact.nameGiven',
            'relContact.nameFamily',
            'relContact.namePreferred',
            'relContact.birthday',
            'relContact.phonePrimary',
            'relContact.emailPrimary',
            'relContact.addressCurrent',
            'relContact.addressMailing',
            'relAnniversaries.type',
            'relAnniversaries.name',
            'relAnniversaries.timeStamp',
            'relAnniversaries.daysDifference'
          ],
          filters: [
            {
              nest: 'relAnniversaries',
              bool: 'must',
              term: 'relAnniversaries.type',
              type: 'string',
              value: 'INS'
            },
            {
              nest: 'relAnniversaries',
              bool: 'must',
              term: 'relAnniversaries.timeStamp',
              type: 'date',
              value: { from: `{timeStampTerm}`, to: `{timeStampTerm}` }
            }
          ]
        },
        {
          key: 'anniversary.settled',
          name: 'Settlement',
          fields: [
            'relContact.owner',
            'relContact.name',
            'relContact.nameGiven',
            'relContact.nameFamily',
            'relContact.namePreferred',
            'relContact.birthday',
            'relContact.phonePrimary',
            'relContact.emailPrimary',
            'relContact.addressCurrent',
            'relContact.addressMailing',
            'relAnniversaries.type',
            'relAnniversaries.name',
            'relAnniversaries.timeStamp',
            'relAnniversaries.daysDifference'
          ],
          filters: [
            {
              nest: 'relAnniversaries',
              bool: 'must',
              term: 'relAnniversaries.type',
              type: 'string',
              value: 'SETTLED'
            },
            {
              nest: 'relAnniversaries',
              bool: 'must',
              term: 'relAnniversaries.month',
              type: 'number',
              value: { from: `{timeStampMonth}`, to: `{timeStampMonth}` }
            }
          ]
        }
      ],
      VALUES: [
        {
          key: 'sum',
          name: 'Deal value'
        },
        {
          key: 'count',
          name: 'Deal count'
        }
      ]
    },
    FIELDS: [
      {
        name: 'Organization - General Information - Address state',
        key: 'relOrganization.organization.address.state',
        type: 'string'
      },
      {
        name: 'Deal - Aggregated Values - Count',
        key: 'aggregation.deal.count',
        type: 'number'
      },
      {
        name: 'Deal - Aggregated Values - Value',
        key: 'aggregation.deal.sum',
        type: 'currency'
      },
      {
        name: 'Contact - Information - Owner Name',
        key: 'relContact.owner',
        type: 'string'
      },
      {
        name: 'Contact - Information - Name',
        key: 'relContact.name',
        type: 'string'
      },
      {
        name: 'Contact - Information - Given Name',
        key: 'relContact.nameGiven',
        type: 'string'
      },
      {
        name: 'Contact - Information - Family Name',
        key: 'relContact.nameFamily',
        type: 'string'
      },
      {
        name: 'Contact - Information - Preferred Name',
        key: 'relContact.namePreferred',
        type: 'string'
      },
      {
        name: 'Contact - Information - Birthday',
        key: 'relContact.birthday',
        type: 'string'
      },
      {
        name: 'Contact - Information - Current Address',
        key: 'relContact.addressCurrent',
        type: 'string'
      },
      {
        name: 'Contact - Information - Mailing Address',
        key: 'relContact.addressMailing',
        type: 'string'
      },
      {
        name: 'Contact - Information - Phone',
        key: 'relContact.phonePrimary',
        type: 'string'
      },
      {
        name: 'Contact - Information - E-Mail',
        key: 'relContact.emailPrimary',
        type: 'string'
      },
      {
        name: 'Contact - Anniversary - Name',
        key: 'relAnniversaries.name',
        type: 'string'
      },
      {
        name: 'Contact - Anniversary - In days',
        key: 'relAnniversaries.daysDifference',
        type: 'number'
      },
      {
        name: 'Contact - Anniversary - Date',
        key: 'relAnniversaries.timeStamp',
        type: 'string'
      }
    ],
    FIELDS_ORDERING: [
      'aggregation.deal.created.count',
      'aggregation.deal.created.sum',
      'aggregation.deal.settled.count',
      'aggregation.deal.settled.sum',
      'relAnniversaries.daysDifference'
    ],
    CACHE: {
      NAME: 'reportsCache',
      STORE: {
        maxAge: 60 * 1000,
        deleteOnExpire: 'aggressive',
        recycleFreq: 30 * 1000,
        storageMode: 'sessionStorage'
      }
    }
  });
